/**
 * v2 トーストシステム (命令的 API)。
 *
 * antd の `message` / `notification` を置き換える。React の外(フック・サービス)からも
 * 呼べるよう、モジュールレベルのシンプルな購読モデルで実装する。
 * `<Toaster />`(src/v2/toast/Toaster.tsx) を App ルートに 1 つマウントして表示する。
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastItem {
  id: string
  type: ToastType
  message: string
  /** 補足説明 (notification の description 相当)。 */
  description?: string
  /** 自動消去までの ms。0 で消さない。 */
  duration: number
  /** 任意のアクションボタン (notification の btn 相当)。 */
  action?: ToastAction
}

export interface ToastOptions {
  description?: string
  /** 秒。未指定なら type 既定 (error/warning=6s, それ以外=3s)。0 で消さない。 */
  durationSec?: number
  action?: ToastAction
}

type Listener = (items: ToastItem[]) => void

const listeners = new Set<Listener>()
let items: ToastItem[] = []
let seq = 0

function notify() {
  for (const l of listeners) l(items)
}

function nextId(): string {
  seq += 1
  return `toast-${seq}-${Date.now()}`
}

function defaultDuration(type: ToastType): number {
  return type === 'error' || type === 'warning' ? 6000 : 3000
}

function push(type: ToastType, message: string, opts?: ToastOptions): string {
  const id = nextId()
  const duration =
    opts?.durationSec != null ? opts.durationSec * 1000 : defaultDuration(type)
  items = [
    ...items,
    { id, type, message, description: opts?.description, duration, action: opts?.action },
  ]
  notify()
  if (duration > 0) {
    setTimeout(() => dismiss(id), duration)
  }
  return id
}

export function dismiss(id: string): void {
  items = items.filter((t) => t.id !== id)
  notify()
}

export function subscribeToasts(listener: Listener): () => void {
  listeners.add(listener)
  listener(items)
  return () => {
    listeners.delete(listener)
  }
}

/** 命令的トースト API (antd message/notification の置換)。 */
export const toast = {
  success: (message: string, opts?: ToastOptions) => push('success', message, opts),
  error: (message: string, opts?: ToastOptions) => push('error', message, opts),
  warning: (message: string, opts?: ToastOptions) => push('warning', message, opts),
  info: (message: string, opts?: ToastOptions) => push('info', message, opts),
  /** notification 互換: type を含むオブジェクト形式。 */
  notify: (args: {
    type?: ToastType
    message: string
    description?: string
    durationSec?: number
    action?: ToastAction
  }) =>
    push(args.type ?? 'info', args.message, {
      description: args.description,
      durationSec: args.durationSec,
      action: args.action,
    }),
}
